import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { BrowseSortControl } from "@/components/browse/BrowseSortControl";
import { BrowseViewToggle } from "@/components/browse/BrowseViewToggle";
import { LiveTableBanner } from "@/components/browse/LiveTableBanner";
import { PricesAsOfLine, PricesUpdatedOverlay } from "@/components/browse/PricesFreshness";
import { PublicBackButton } from "@/components/browse/PublicBackButton";
import { PublicBrowseSkeleton } from "@/components/browse/PublicBrowseSkeleton";
import { PublicGridCard } from "@/components/browse/PublicGridCard";
import { PublicListRow } from "@/components/browse/PublicListRow";
import { PublicPageFooter } from "@/components/browse/PublicPageFooter";
import { StorefrontIdentity } from "@/components/browse/StorefrontIdentity";
import {
  StorefrontListShell,
  StorefrontLoadError,
  StorefrontToolbar,
} from "@/components/browse/StorefrontListShell";
import { TableArrivalChooser } from "@/components/browse/TableArrivalChooser";
import { AppliedFilterChips } from "@/components/filters/AppliedFilterChips";
import { FilterPill } from "@/components/filters/FilterPill";
import { StorefrontFollowSlot } from "@/components/follows/StorefrontFollowSlot";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { FlashList, type ListRef } from "@/components/ui/FlashList";
import { StorefrontWantMatchSlot } from "@/components/wants/StorefrontWantMatchSlot";
import { useCollapsingHeader } from "@/hooks/useCollapsingHeader";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useMarkInteractive } from "@/hooks/useMarkInteractive";
import {
  type PublicBrowseItem,
  PublicItemNotListedError,
  usePublicShopItem,
} from "@/hooks/usePublicBrowse";
import { usePublicBrowseView } from "@/hooks/usePublicBrowseView";
import {
  type PublicInventoryItem,
  usePublicInventory,
  usePublicInventoryGames,
} from "@/hooks/usePublicInventory";
import { useStorefrontFilters } from "@/hooks/useStorefrontFilters";
import { useStorefrontGrid } from "@/hooks/useStorefrontGrid";
import {
  browseFacetParams,
  type BrowseSort,
  filterAndSortBrowse,
  hasBrowseFilters,
  hasPriceBounds,
  needsWholeTable,
  refinementFilters,
} from "@/lib/browse/browseFilter";
import { loadShopAnalytics } from "@/lib/browse/shopAnalyticsClient";
import { gameLabel, storefrontFacets } from "@/lib/browse/storefrontFacets";
import { gridGutters } from "@/lib/inventory/desktopGrid";
import { buylistPath, shopCardPath, tablePath } from "@/lib/shop/publicUrls";
import { slopToFloor } from "@/lib/ui/touchTarget";

const toBrowseItem = (item: PublicInventoryItem): PublicBrowseItem => ({
  public_id: item.public_id,
  product_name: item.name,
  qty: item.qty ?? 1,
  condition: item.condition ?? null,
  is_graded: item.is_graded ?? false,
  grading_company: item.grading_company ?? null,
  grade: item.grade ?? null,
  image_url: item.image_url,
  product_type: item.product_type ?? null,
  set_name: item.set_name ?? null,
  number: item.number ?? null,
  variant_label: item.variant ?? null,
  game: item.game ?? null,
  rarity: item.rarity ?? null,
  price: item.ask ?? null,
  currency: item.currency ?? null,
});

export default function PublicInventoryBrowseScreen() {
  // `item` is the card a scanned label QR asked for (TRO-483). When the vendor is not operating a
  // show, the backend redirects `/c/<public_id>` here with `?item=<public_id>`, landing the customer
  // on the vendor's book rather than TCGplayer. It is a query param, not a route segment, because
  // this destination is a browse, not a detail view (ADR-0056).
  const { slug, item: scannedId } = useLocalSearchParams<{ slug: string; item?: string; }>();
  const [query, setQuery] = useState("");
  // Search runs on the server, so a typed word is one request; the box stays responsive on `query`.
  const search = useDebouncedValue(query, 250);
  // Dismissing the scan is tracked as a flag rather than by clearing the id, and `scanned` is
  // derived, so a new scan (a navigation with a new `item`) shows the new card.
  const [showAll, setShowAll] = useState(false);
  const [followFailure, setFollowFailure] = useState<string | null>(null);
  // Whether the gate has put its Follow button on screen: the phone's name row stays pinned
  // exactly then, so an anonymous visitor, or one without the flag, scrolls the page as before.
  const [followShown, setFollowShown] = useState(false);
  // The loaded units that meet one of the signed-in person's Wants, reported by the match slot;
  // `null` for an anonymous visitor, with the flag off, or while the answer is not in.
  const [wantMatched, setWantMatched] = useState<ReadonlySet<string> | null>(null);
  const scanned = showAll ? null : (scannedId ?? null);
  const [sort, setSort] = useState<BrowseSort>("featured");
  // The table's Filters (TRO-1095) on the shop too: one storefront, one set of controls.
  const storefrontFilters = useStorefrontFilters();
  const { game: gameSel, set: setSel, stockGroup, refinements, applyFacetPatch } =
    storefrontFilters;
  // The card-art grid for a first-time visitor (TRO-647); the compact header leaves room for a row
  // of cards with their prices above the fold.
  const [layout, setLayout] = usePublicBrowseView("grid");
  // Set when a customer asks for the table while the vendor runs several, so the screen asks which
  // (ADR-0138). One table needs no question; the banner goes straight there.
  const [choosingTable, setChoosingTable] = useState(false);
  const insets = useSafeAreaInsets();
  const { wide, maxContentWidth, width, horizontalPadding, gap, columns, cellWidth } =
    useStorefrontGrid();
  const collapse = useCollapsingHeader(insets.top, !wide && followShown);
  // A scan narrows to one card, and the customer is checking its art, so it shows as one grid cell
  // at the card-detail hero width (240pt, `CardTile`'s `HERO_WIDTH`), not a small cell or row, and
  // not full width, which would be taller than the viewport. The layout toggle applies again after
  // widening.
  const grid = layout === "grid" || !!scanned;
  const listColumns = grid && !scanned ? columns : 1;
  const scannedCellWidth = Math.min(240, width - horizontalPadding * 2);
  const gutters = gridGutters({
    hPad: horizontalPadding,
    vPad: 0,
    colGap: grid && !scanned ? gap : 0,
    // List rows draw their own hairline, so only the grid needs a row gap.
    rowGap: grid ? gap : 0,
  });

  const filters = useMemo(
    () => ({ game: gameSel, set: setSel, ...refinementFilters(refinements) }),
    [gameSel, setSel, refinements],
  );
  // A Location is selected by its StockGroup's name, as the table's is, so two groups sharing a
  // name filter together and the server needs no id lookup.
  const inventory = usePublicInventory(slug ?? null, search, sort, { ...filters, stockGroup });
  // What the match preview counts under: the search and the column facets the inventory read
  // sends. Game, set and price are not sent to the preview's count, which does not narrow on them.
  const previewFacets = useMemo(
    () => browseFacetParams({ ...filters, stockGroup, game: undefined, set: undefined }),
    [filters, stockGroup],
  );
  const loadedIds = useMemo(
    () => (inventory.data?.items ?? []).map((item) => item.public_id),
    [inventory.data?.items],
  );
  const groupNames = useMemo(
    () => new Map((inventory.data?.stock_groups ?? []).map((g) => [g.public_id, g.name])),
    [inventory.data?.stock_groups],
  );
  // Cold-launched from shared links, and from `shops/[slug]`, which re-exports this component.
  useMarkInteractive(!inventory.isPending);
  // The chips span the whole shop, not the loaded pages.
  const { games, sets } = usePublicInventoryGames(slug ?? null);
  // The scanned card is read by its own id, since it can sit on any page.
  const scan = usePublicShopItem(slug ?? null, scanned);
  const facets = useMemo(
    () =>
      storefrontFacets({
        games,
        sets,
        game: gameSel,
        set: setSel,
        refinements,
        showPrice: true,
        // Locations are the vendor's own shelving, so on the shop they live under Filters and the
        // buyer's own way in, the game, takes the top row.
        locations: [...new Set((inventory.data?.stock_groups ?? []).map((g) => g.name))].map((
          name,
        ) => ({ value: name, label: name })),
        location: stockGroup,
      }),
    [games, sets, gameSel, setSel, refinements, inventory.data?.stock_groups, stockGroup],
  );

  const scannedCard = scan.data?.item;
  // A scan that failed for any reason but a 404 says nothing about the card, so it is neither
  // "sold" nor a metric.
  const scanFailed = !!scan.error && !(scan.error instanceof PublicItemNotListedError);
  // A walked price order is only right once every page is in, so the grid waits rather than
  // reshuffling under the shopper as each page lands. A server-sorted one is right page by page.
  const sortWalking = !scanned && sort.startsWith("price-") && inventory.walking
    && inventory.isLoadingRest;
  const items = useMemo(() => {
    // The server already searched and narrowed the pages; a scan is one card read on its own, so
    // the typed search is matched against it here.
    const needle = query.trim().toLocaleLowerCase();
    const visible = scanned
      ? (scannedCard ? [scannedCard] : []).filter((item) =>
        !needle || [
          item.product_name,
          item.set_name,
          item.number,
          item.variant_label,
          item.condition,
          item.game,
        ].some((value) => value?.toLocaleLowerCase().includes(needle))
      )
      : (inventory.data?.items ?? []).filter((item) =>
        !stockGroup || groupNames.get(item.stock_group_public_id) === stockGroup
      ).map(toBrowseItem);
    return sortWalking
      ? []
      : filterAndSortBrowse(visible, sort, filters, { serverOrdered: inventory.serverPriced });
  }, [
    stockGroup,
    groupNames,
    inventory.data?.items,
    scannedCard,
    query,
    scanned,
    sort,
    filters,
    sortWalking,
    inventory.serverPriced,
  ]);
  const listRef = useRef<ListRef<PublicBrowseItem>>(null);

  // Loaded once per page view and held in a ref (ADR-0076): each visit gets a fresh anonymous id,
  // so reloading per render would create a new visitor per keystroke.
  const analytics = useRef<Awaited<ReturnType<typeof loadShopAnalytics>> | null>(null);
  const reported = useRef({ opened: false, scanOutcome: false, widened: false });

  useEffect(() => {
    let cancelled = false;
    void loadShopAnalytics().then((loaded) => {
      if (!cancelled) analytics.current = loaded;
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const loaded = !inventory.isPending && !!inventory.data;
  const itemCount = inventory.data?.shop_total ?? 0;
  const arrivedWithScan = !!scannedId;

  useEffect(() => {
    if (!loaded || !slug || reported.current.opened) return;
    reported.current.opened = true;
    // Classify `document.referrer` rather than sending it: it can carry another site's query string,
    // and ADR-0061's no-typed-text rule applies on every surface.
    const referrerClass = typeof document === "undefined" || !document.referrer
      ? "direct"
      : document.referrer.includes(window.location.host)
      ? "internal"
      : "external";
    analytics.current?.shopOpened(slug, arrivedWithScan, itemCount, referrerClass);
  }, [arrivedWithScan, itemCount, loaded, slug]);

  useEffect(() => {
    // Reports the scan's own outcome once. A miss counts only when the scan is the sole narrowing;
    // with a search, StockGroup or other filter also active, an empty list is not evidence of a sale.
    if (!loaded || !slug || !scannedId || reported.current.scanOutcome) return;
    if (scan.isLoading || scanFailed) return;
    if (scannedCard) {
      reported.current.scanOutcome = true;
      analytics.current?.scanLanded(slug);
      return;
    }
    // The same condition as `scannedOnly` below, repeated because that binding comes after the
    // early returns, where a hook cannot read it.
    const scanIsOnlyNarrowing = !stockGroup && !query.trim() && !showAll
      && !hasBrowseFilters(filters);
    if (!scanIsOnlyNarrowing) return;
    reported.current.scanOutcome = true;
    // The backend redirects here only for this shop's labels, so an absent card was sold or
    // unpublished.
    analytics.current?.scanMissed(slug, "sold");
  }, [
    stockGroup,
    filters,
    scan.isLoading,
    scanFailed,
    scannedCard,
    loaded,
    query,
    scannedId,
    showAll,
    slug,
  ]);

  // A remounted list (column change) or an emptied one starts at the top, identity showing.
  const { reset } = collapse;
  const emptied = items.length === 0;
  useEffect(reset, [reset, listColumns, emptied]);

  if (inventory.isLoading) return <PublicBrowseSkeleton layout={layout} />;
  if (inventory.error || !inventory.data) {
    return (
      <StorefrontLoadError
        title="Couldn't load this inventory"
        subtitle="This public link may be off. Check the link and try again."
        onRetry={() => inventory.refetch()}
      />
    );
  }

  if (scanFailed) {
    return (
      <StorefrontLoadError
        title="Couldn't load that card"
        subtitle="Check your connection and try again."
        onRetry={() => scan.refetch()}
      />
    );
  }

  const liveTables = inventory.data.live_tables ?? [];
  const openTable = (eventId: string) => router.push(tablePath(slug, eventId));
  // A card opens its own page, where the customer compares its price as at a table.
  const openCard = (itemId: string) => router.push(shopCardPath(slug, itemId));
  // The chooser replaces the book rather than sitting above it: the customer asked to go to a
  // table, and a list of cards below the question is a second thing to read before answering.
  const choosing = choosingTable && liveTables.length > 1;

  // Every listed unit, whatever is typed or filtered; the matches are counted apart from it.
  const total = inventory.data.shop_total;
  const narrowed = !!stockGroup || !!query.trim() || !!scanned || hasBrowseFilters(filters);
  // The server counts its own search and facets (game and set where it lists them, TRO-2347); a
  // client-only facet (game and set from an older server, and price where the server did not take
  // the bounds) narrows rows it counted, so its count is the rows left once
  // the loaded pages are in. A scan is one card.
  const clientNarrowed = needsWholeTable(filters, inventory.serverFacets)
    || (inventory.boundsOnClient && hasPriceBounds(filters));
  const matched = scanned || clientNarrowed ? items.length : inventory.data.total;
  // The matched count comes from the page on screen. Until the typed text has been sent and
  // answered, that page is the previous query's, so its total does not describe this narrowing.
  const staleCount = inventory.countStale || query.trim() !== search.trim();
  const counting = !scanned
    && (sortWalking || (narrowed && (staleCount || (clientNarrowed && inventory.isLoadingRest))));
  // True while typed text has not been debounced and sent yet, so the screen never says "no cards
  // match" for a query nobody has asked the server about.
  const busy = scanned
    ? scan.isLoading
    : inventory.isLoadingRest || query.trim() !== search.trim();
  // The sold-card empty state applies only when the scan alone empties the list. Search, StockGroup
  // and the other filters stay usable during a scan and combine with it, so a miss they cause must
  // not be blamed on a sale.
  const scannedOnly = !!scanned && !stockGroup && !query.trim() && !hasBrowseFilters(filters);
  const productCount = counting
    ? "Counting products…"
    : `${
      narrowed
        ? `${matched.toLocaleString()} of ${total.toLocaleString()}`
        : total.toLocaleString()
    } ${total === 1 ? "product" : "products"}`;
  const refreshPrices = () => {
    inventory.refreshPrices();
    listRef.current?.scrollToOffset({ offset: 0, animated: true });
  };
  const pricesUpdated = inventory.pricesUpdated && !scanned && !choosing;
  const seeEverything = () => {
    if (!reported.current.widened && slug) {
      reported.current.widened = true;
      analytics.current?.widened(slug, !!scannedId);
    }
    setShowAll(true);
    setQuery("");
    storefrontFilters.clear();
  };

  // Search and filters stay pinned above the list, so a large book needs no scroll back to
  // re-search, while the shop's identity scrolls away to give the cards the screen.
  const header = (
    <View
      className="gap-3 border-b border-border bg-bg px-4 pb-3"
      style={{ paddingTop: insets.top + (wide ? 24 : 16) }}
    >
      <View {...collapse.collapsible} className="gap-4">
        <StorefrontIdentity
          name={inventory.data.display_name}
          logoUrl={inventory.data.shop_logo_url}
          tagline={inventory.data.tagline}
          bio={inventory.data.bio}
          operatingArea={inventory.data.operating_area}
          tags={inventory.data.tags}
          links={inventory.data.links}
          wide={wide}
          subtitle={productCount}
          back={<PublicBackButton />}
          follow={slug && !wide
            ? (
              <StorefrontFollowSlot
                slug={slug}
                onFailure={setFollowFailure}
                onShown={setFollowShown}
              />
            )
            : undefined}
          row={collapse.sticky}
          bar={collapse.collapsed}
        />
        {slug && wide ? <StorefrontFollowSlot slug={slug} /> : null}
      </View>
      {
        /* Outside the collapsible part, so it stays on screen under the pinned name row, and
        full width, so it never narrows the name. Phone only, like the slot that raises it: the
        wide layout has no place for this line. */
      }
      {!wide && followFailure
        ? (
          <Text accessibilityRole="alert" className="text-sm font-body text-loss">
            {followFailure}
          </Text>
        )
        : null}

      {/* Pinned, so a customer deep in the book can still cross to the table they're at. */}
      <LiveTableBanner
        tables={liveTables}
        onOpen={openTable}
        onChoose={() => setChoosingTable(true)}
      />
      {inventory.data.buylist_published && slug
        ? (
          <Pressable
            accessibilityRole="link"
            onPress={() => router.push(buylistPath(slug))}
            className="min-h-12 flex-row items-center rounded-xl border border-border bg-surface px-4"
          >
            <Text className="font-label text-sm text-foreground">We're buying →</Text>
          </Pressable>
        )
        : null}

      {
        /* The scanned card narrows the list, so say so and keep the rest of the book one tap
          away. */
      }
      {scanned && !choosing
        ? (
          <View
            testID="public-inventory-scanned-banner"
            className="flex-row items-center justify-between gap-3 rounded-xl border border-primary bg-accent-soft px-4 py-2"
          >
            <Text className="min-w-0 flex-1 text-sm font-body text-primary">
              Showing the card you scanned
            </Text>
            <Pressable
              onPress={seeEverything}
              accessibilityRole="button"
              accessibilityLabel="Show all cards"
              hitSlop={slopToFloor(44)}
              className="min-h-11 justify-center"
            >
              <Text className="text-sm font-label text-primary">
                See everything
              </Text>
            </Pressable>
          </View>
        )
        : null}

      {choosing
        ? null
        : total
        ? (
          // The table's toolbar: search beside Filters and the view switch, then sort, the
          // applied-filter chips and the Locations in one row that scrolls sideways.
          <StorefrontToolbar
            query={query}
            onQueryChange={setQuery}
            filters={{
              facets,
              onChange: applyFacetPatch,
              refinements,
              showPrice: true,
              resultCount: matched,
              onClearAll: storefrontFilters.clear,
            }}
            layout={layout}
            onLayoutChange={setLayout}
          >
            {
              /* Sort, then the buyer's own way in, one tap per game, then a removable chip per
                other filter, in one row that scrolls sideways. The selected game is its pill, so
                it gets no chip as well. */
            }
            <View className="-mx-4 flex-row items-center">
              <ScrollView
                testID="shop-control-row"
                horizontal
                showsHorizontalScrollIndicator={false}
                className="min-w-0 flex-1"
                contentContainerClassName="items-center gap-2 px-4"
              >
                <BrowseSortControl sort={sort} onChange={setSort} />
                {games.length > 1
                  ? (
                    <>
                      <View aria-hidden className="mx-1 h-6 w-px bg-border" />
                      {games.map((g) => (
                        <FilterPill
                          key={g}
                          label={gameLabel(g)}
                          a11yLabel={`Filter by ${gameLabel(g)}`}
                          active={gameSel === g}
                          // A second tap clears it, the quick way back to every game; picking one
                          // drops a Set from another game, as the sheet's Game row does.
                          onPress={() =>
                            applyFacetPatch({
                              game: gameSel === g ? null : g,
                              ...(setSel && setSel.game !== g ? { set: null } : {}),
                            })}
                        />
                      ))}
                    </>
                  )
                  : null}
                <AppliedFilterChips
                  facets={games.length > 1 ? facets.filter((f) => f.key !== "game") : facets}
                  onChange={applyFacetPatch}
                />
              </ScrollView>
              {wide
                ? null
                : (
                  <Text className="flex-none pl-2 pr-4 text-sm font-body text-muted-foreground">
                    {productCount}
                  </Text>
                )}
            </View>
            {
              /* Said aloud as the list narrows: the count in the header scrolls away, and a
                screen-reader user typing a search heard nothing. */
            }
            <Text
              accessibilityLiveRegion="polite"
              aria-live="polite"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                overflow: "hidden",
                opacity: 0,
              }}
            >
              {narrowed && !counting
                ? `${matched.toLocaleString()} of ${total.toLocaleString()} products`
                : ""}
            </Text>
          </StorefrontToolbar>
        )
        : <BrowseViewToggle layout={layout} onChange={setLayout} />}
    </View>
  );

  // One always-mounted list with the empty copy as `ListEmptyComponent`, not a ternary, so the list
  // is not torn down on the keystroke that narrows to zero matches. Both backing lists render the
  // empty component inside their content container.
  const book = (
    <FlashList
      ref={listRef}
      testID="public-inventory-list"
      // Keyed on the column count alone: RN's FlatList (the web build) asserts `numColumns`
      // never changes on a mounted list, and a toggle that keeps the count need not remount.
      key={listColumns}
      data={items}
      // The badges arrive after the rows, so a changed answer re-renders the cells.
      extraData={wantMatched}
      keyExtractor={(item) => item.public_id}
      numColumns={listColumns}
      onEndReached={inventory.loadMore}
      ListEmptyComponent={
        <View className="flex-1" accessibilityLiveRegion="polite">
          <EmptyState
            mark
            title={busy
              ? "Finding cards…"
              : total
              ? (scannedOnly ? "That card isn't listed" : "No cards match")
              : "Nothing to browse yet"}
            subtitle={busy
              ? undefined
              : total
              // A scanned card that sold or was unpublished gets its own copy, since the
              // customer typed no search. With a search or StockGroup, the ordinary copy
              // applies.
              ? (scannedOnly
                ? "It may have just sold. Here's everything else this shop has."
                : "Try another search, filter or Location.")
              : "This shop is still choosing what to share."}
            action={narrowed && !busy
              ? <Button title="Show all cards" variant="outline" onPress={seeEverything} />
              : undefined}
          />
        </View>
      }
      {...collapse.list}
      // On the web, a row focused by Tab scrolls clear of the pinned header instead of under it.
      style={Platform.OS === "web"
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- a CSS property react-native-web passes through; RN's ViewStyle has no key for it.
        ? ({ scrollPaddingTop: collapse.headerHeight } as ViewStyle)
        : undefined}
      // The signed-in person's Want matches sit above the cards and scroll with them, outside the
      // collapsing header, so they never change what the pinned part measures.
      ListHeaderComponent={slug
        ? (
          <View
            style={{ paddingHorizontal: gutters.cellHPad, paddingVertical: gutters.cellVPad }}
          >
            {pricesUpdated ? null : <PricesAsOfLine pricedAsOf={inventory.pricedAsOf} />}
            <StorefrontWantMatchSlot
              slug={slug}
              search={search}
              facets={previewFacets}
              // The summary is counted with the server facets alone, so any game, set or price
              // filter shows the shop-wide count (TRO-1931 D6), wherever the cards are bounded.
              clientNarrowed={needsWholeTable(filters) || hasPriceBounds(filters)}
              itemIds={loadedIds}
              onMatchedItems={setWantMatched}
            />
          </View>
        )
        : undefined}
      // Scrolls with the cards rather than pinned under them, so it costs no room until the end.
      ListFooterComponent={<PublicPageFooter />}
      // `flexGrow` lets the empty state fill the screen; a content container sized to its
      // content would collapse it.
      contentContainerStyle={{
        flexGrow: 1,
        paddingHorizontal: gutters.contentHPad,
        paddingTop: collapse.headerHeight + gutters.cellVPad,
      }}
      // The cell draws both gutters, not `columnWrapperStyle` or a container `gap` (see
      // `gridGutters`).
      renderItem={({ item }) => (
        <View
          style={{
            paddingHorizontal: gutters.cellHPad,
            paddingVertical: gutters.cellVPad,
          }}
        >
          {grid
            ? (
              <View className={scanned ? "items-center" : undefined}>
                <PublicGridCard
                  item={item}
                  matchesWant={wantMatched?.has(item.public_id) ?? false}
                  width={scanned ? scannedCellWidth : cellWidth}
                  onPress={() => openCard(item.public_id)}
                />
              </View>
            )
            : (
              <PublicListRow
                item={item}
                matchesWant={wantMatched?.has(item.public_id) ?? false}
                onPress={() => openCard(item.public_id)}
              />
            )}
        </View>
      )}
    />
  );

  return (
    <StorefrontListShell
      testID="public-inventory-browse"
      headerTestID="public-inventory-header"
      maxContentWidth={maxContentWidth}
      collapse={collapse}
      header={header}
    >
      {choosing
        ? (
          <View className="flex-1" style={{ paddingTop: collapse.headerHeight }}>
            <TableArrivalChooser tables={liveTables} onChoose={openTable} />
            <PublicPageFooter />
          </View>
        )
        : book}
      {pricesUpdated
        ? (
          <PricesUpdatedOverlay
            headerHeight={collapse.headerHeight}
            headerStyle={collapse.header.style}
            onPress={refreshPrices}
          />
        )
        : null}
    </StorefrontListShell>
  );
}
